/**
 * Shared factory functions for message store operations.
 *
 * Extracted from useLocalMessageStore and useZustandMessageStore which
 * had identical addUserMessage and createStreamingMessage implementations.
 */

import { MessageRole } from '@/services/chat';
import type { Message } from '@/stores/useAppStore';

/**
 * Create a user message with dedup guard against repeated submissions.
 * Returns the existing message if duplicate detected, or the new message.
 * Pass `id` to use the client message id the send carries, so a Retry or Edit
 * can name this message to the server before its row id is known.
 */
export function buildUserMessage(
  content: string,
  currentMessages: Message[],
  artifacts?: Message['artifacts'],
  id?: string
): { message: Message; isDuplicate: boolean } {
  const trimmed = content.trim();

  if (currentMessages.length > 0) {
    const last = currentMessages[currentMessages.length - 1];
    if (last && last.role === MessageRole.USER && last.content.trim() === trimmed) {
      return { message: last, isDuplicate: true };
    }
  }

  return {
    message: {
      id: id ?? crypto.randomUUID(),
      role: MessageRole.USER,
      content: trimmed,
      sentAt: Date.now(),
      ...(artifacts && artifacts.length > 0 ? { artifacts } : {}),
    },
    isDuplicate: false,
  };
}

/**
 * Create an empty streaming assistant placeholder message.
 *
 * @param format - Optional per-turn render format. When 'html', the message
 *   is seeded with an empty `htmlEvents` array so live tokenizer events have a
 *   stable target. Defaults to undefined (markdown path; no htmlEvents). The
 *   format also arrives on `stream_start`, which patches it onto the message;
 *   threading it here covers the optimistic-creation path.
 */
export function buildStreamingMessage(format?: 'markdown' | 'html'): Message {
  return {
    id: crypto.randomUUID(),
    role: MessageRole.ASSISTANT,
    content: '',
    isStreaming: true,
    turnStartedAt: Date.now(),
    sentAt: Date.now(),
    ...(format ? { format } : {}),
    ...(format === 'html' ? { htmlEvents: [] } : {}),
  };
}
